// TimeTrackingApp.js - Mit API-Caching und verbesserten Fehlerbehandlungen
import React, { useState, useEffect } from 'react';
import WeekNavigation from './WeekNavigation';
import NewEntryForm from './NewEntryForm';
import EditEntryForm from './EditEntryForm';
import EntriesTable from './EntriesTable';
// AdminPanel wurde nach /admin verschoben
import { getWeekDates, formatDateForAPI, parseDateAsLocal } from '../utils/dateUtils';
import { API_BASE_URL } from '../config/apiConfig';
import { saveEntryToBackup } from '../utils/localBackup';

const TimeTrackingApp = ({ user, onLogout }) => {
  // States
  const [currentWeek, setCurrentWeek] = useState(() => {
    // Setze initiale Woche auf die aktuelle Woche
    const initialDate = new Date();
    console.log('Setze initiale Woche auf:', initialDate);
    return initialDate;
  });
  const [entries, setEntries] = useState([]);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [auftraege, setAuftraege] = useState([]);
  const [stundenarten, setStundenarten] = useState([]);
  const [auftragUsage, setAuftragUsage] = useState({});
  const [viewMode, setViewMode] = useState('card');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Ref für das Scrollen

  // Toggle view mode
  const toggleViewMode = () => {
    const newMode = viewMode === 'card' ? 'list' : 'card';
    setViewMode(newMode);
  };

  // Navigation handlers
  const handlePreviousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };

  // Lade Aufträge
  useEffect(() => {
    const fetchAuftraege = async () => {
      try {
        const url = `${API_BASE_URL}/tenant/tblAuftraege?filter=all`;
        console.log('Starte Laden der Aufträge von:', url);

        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Tenant-Key': user.tenant || user.database
          }
        });
        console.log('API Response Status (Aufträge):', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API-Fehler:', response.status, response.statusText, errorText);
          throw new Error(`Fehler beim Laden der Aufträge: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('Geladene Aufträge (roh):', result);

        // Backend gibt { success: true, data: [...] } zurück
        const data = result.data || result;
        if (!Array.isArray(data)) {
          console.error('Unerwartetes Datenformat:', result);
          throw new Error('Unerwartetes Datenformat für Aufträge');
        }

        // Verarbeite die Aufträge - Map SQLite-Felder zu Frontend-Format
        const processedData = data
          .map(auftrag => ({
            AufID: auftrag.aufid,
            AufNr: String(auftrag.aufnr || ''),
            AufBez: String(auftrag.aufobjekt || ''),
            AufObjekt: String(auftrag.aufobjekt || ''),
            AufAktiv: Boolean(auftrag.aufaktiv),
            AufAbschluss: Boolean(auftrag.aufabschluss)
          }))
          .sort((a, b) => {
            if (!a.AufNr) return 1;
            if (!b.AufNr) return -1;
            return String(a.AufNr).localeCompare(String(b.AufNr));
          });
        
        console.log('Verarbeitete Aufträge:', processedData);
        setAuftraege(processedData);
      } catch (err) {
        console.error('Fehler beim Laden der Aufträge:', err);
        setError('Fehler beim Laden der Aufträge: ' + err.message);
      }
    };
    fetchAuftraege();
  }, []);

  // Lade Stundenarten
  useEffect(() => {
    const fetchStundenarten = async () => {
      try {
        const url = `${API_BASE_URL}/tenant/tblSart`;
        console.log('Starte Laden der Stundenarten von:', url);

        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Tenant-Key': user.tenant || user.database
          }
        });
        console.log('API Response Status (Stundenarten):', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API-Fehler:', response.status, response.statusText, errorText);
          throw new Error(`Fehler beim Laden der Stundenarten: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('Geladene Stundenarten (roh):', result);

        // Backend gibt { success: true, data: [...] } zurück
        const data = result.data || result;
        if (!Array.isArray(data)) {
          console.error('Unerwartetes Datenformat:', result);
          throw new Error('Unerwartetes Datenformat für Stundenarten');
        }

        // Verarbeite die Stundenarten - Map SQLite-Felder zu Frontend-Format
        const processedData = data
          .map(art => ({
            Sart: art.sart,
            SarBezeichnung: String(art.sarbezeichnung || ''),
            SarSatz: parseFloat(art.sarsatz || 0)
          }))
          .sort((a, b) => {
            if (!a.SarBezeichnung) return 1;
            if (!b.SarBezeichnung) return -1;
            return a.SarBezeichnung.localeCompare(b.SarBezeichnung);
          });
        
        console.log('Verarbeitete Stundenarten:', processedData);
        setStundenarten(processedData);
      } catch (err) {
        console.error('Fehler beim Laden der Stundenarten:', err);
        setError('Fehler beim Laden der Stundenarten: ' + err.message);
      }
    };
    fetchStundenarten();
  }, []);

  // Lade Einträge für die aktuelle Woche
  useEffect(() => {
    const fetchEntries = async () => {
      try {
        setLoading(true);
        const { startDate, endDate } = getWeekDates(currentWeek);
        const formattedStartDate = formatDateForAPI(startDate);
        const formattedEndDate = formatDateForAPI(endDate);
        
        console.log('Aktuelle Woche:', currentWeek);
        console.log('Berechnete Wochendaten:', { startDate, endDate });
        console.log('Formatierte Daten für API:', { formattedStartDate, formattedEndDate });
        console.log('User ID:', user.mitid);
        
        // Prüfe, ob die Aufträge und Stundenarten bereits geladen sind
        if (auftraege.length === 0 || stundenarten.length === 0) {
          console.log('Warte auf Aufträge und Stundenarten...');
          return;
        }
        
        const url = `${API_BASE_URL}/tenant/arbeitszeiten/${user.mitid}`;
        console.log('Lade Einträge von:', url);

        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Tenant-Key': user.tenant || user.database
          }
        });
        
        console.log('API Response Status (Einträge):', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API-Fehler:', response.status, response.statusText, errorText);
          throw new Error(`Fehler beim Laden der Einträge: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('Geladene Einträge (roh):', result);

        // Backend gibt { success: true, data: [...] } zurück
        const data = result.data || result;
        if (!Array.isArray(data)) {
          console.error('Unerwartetes Datenformat:', result);
          throw new Error('Unerwartetes Datenformat: Keine Array-Struktur');
        }

        // Zähle Auftrag-Häufigkeit aus ALLEN Einträgen (vor Wochenfilter)
        const usage = {};
        data.forEach(entry => {
          const aufId = entry.stuaufidref;
          if (aufId) {
            usage[aufId] = (usage[aufId] || 0) + 1;
          }
        });
        setAuftragUsage(usage);

        // Verarbeite die Einträge mit den korrekten Feldnamen
        const userMitId = parseInt(user.mitid, 10);
        console.log('Filter-Parameter:', { userMitId, formattedStartDate, formattedEndDate, dataLength: data.length });

        const processedData = data
          .filter(entry => {
            const entryMitId = parseInt(entry.stumitidref, 10);
            const mitIdMatch = entryMitId === userMitId;

            // Prüfe Datum - Parse ISO-String und hole lokales Datum
            // '2026-02-01T23:00:00.000Z' (UTC) = '2026-02-02' in CET
            const entryDate = new Date(entry.studatum);
            const entryYear = entryDate.getFullYear();
            const entryMonth = String(entryDate.getMonth() + 1).padStart(2, '0');
            const entryDay = String(entryDate.getDate()).padStart(2, '0');
            const entryDateStr = `${entryYear}-${entryMonth}-${entryDay}`;

            const dateMatch = entryDateStr >= formattedStartDate && entryDateStr <= formattedEndDate;

            // Debug: Zeige warum Einträge gefiltert werden
            if (!mitIdMatch || !dateMatch) {
              console.log('Eintrag gefiltert:', {
                stuwebid: entry.stuwebid,
                entryMitId, userMitId, mitIdMatch,
                rawDate: entry.studatum, entryDateStr, formattedStartDate, formattedEndDate, dateMatch
              });
            }

            return mitIdMatch && dateMatch;
          })
          .map(entry => ({
            StuPGID: entry.stuwebid,
            StuDatum: entry.studatum ? entry.studatum.split('T')[0] : '',
            StuAnzahl: parseFloat(entry.stuanzahl) || 0,
            StuBeschrieb: entry.stubeschrieb || '',
            StuAufIDRef: entry.stuaufidref,
            StuSarIDRef: entry.stusaridref,
            StuErfassDatum: entry.stuerfassdatum,
            StuVerrechnet: entry.stuverrechnet,
            StuZuschlag: entry.stuzuschlag,
            StuMank: entry.stumank
          }))
          .sort((a, b) => new Date(a.StuDatum) - new Date(b.StuDatum));
        
        console.log('Verarbeitete und gefilterte Einträge:', processedData);
        setEntries(processedData);
        setError(null);
      } catch (err) {
        console.error('Fehler beim Laden der Einträge:', err);
        setError(`Fehler beim Laden der Einträge: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchEntries();
  }, [currentWeek, user.mitid, auftraege.length, stundenarten.length]);

  // Stelle die Datenbankverbindung wieder her
  useEffect(() => {
    const reconnectDatabase = async () => {
      try {
        console.log('Versuche Datenbankverbindung wiederherzustellen...');
        const response = await fetch(`${API_BASE_URL}/reconnect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ database: localStorage.getItem('lastDatabase') }),
        });

        if (!response.ok) {
          if (response.status === 404) {
            console.log('Reconnect-Endpunkt nicht gefunden - fahre fort...');
            return;
          }
          const errorText = await response.text();
          console.error('Server-Antwort:', errorText);
          throw new Error(`Fehler ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log('Datenbankverbindung erfolgreich wiederhergestellt:', data);
      } catch (err) {
        console.error('Fehler beim Wiederherstellen der Verbindung:', err);
        // Nur ausloggen, wenn es sich nicht um einen 404-Fehler handelt
        if (!err.message.includes('404')) {
          setError('Fehler beim Wiederherstellen der Datenbankverbindung. Bitte neu anmelden.');
          onLogout();
        }
      }
    };

    // Nur reconnect versuchen, wenn eine Datenbank ausgewählt wurde
    const selectedDatabase = localStorage.getItem('lastDatabase');
    if (selectedDatabase) {
      reconnectDatabase();
    }
  }, [onLogout]);

  // Hilfsfunktion zur Überprüfung des Datums
  const isDateWithinRange = (dateString) => {
    // parseDateAsLocal verhindert UTC-Timezone-Verschiebung
    const date = parseDateAsLocal(dateString);
    const today = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(today.getDate() - 10);
    return date >= tenDaysAgo;
  };

  const handleNewEntry = async (newEntry) => {
    try {
      // Validiere die Daten
      if (!newEntry.stuaufidref || !newEntry.stusaridref || !newEntry.studatum || !newEntry.stuanzahl) {
        throw new Error('Fehlende Pflichtfelder');
      }

      // Prüfe das Datum
      if (!isDateWithinRange(newEntry.studatum)) {
        throw new Error('Einträge können nur für die letzten 10 Tage erstellt werden');
      }

      // Formatiere das Datum korrekt für die API (nur Datum ohne Zeitzone)
      const dateParts = newEntry.studatum.split('-');
      const formattedDate = `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`;

      // Erstelle das API-Entry-Objekt mit den korrekten Feldnamen
      const apiEntry = {
        stumitidref: parseInt(user.mitid, 10),
        stuaufidref: parseInt(newEntry.stuaufidref, 10),
        stusaridref: parseInt(newEntry.stusaridref, 10),
        studatum: formattedDate,
        stuanzahl: parseFloat(newEntry.stuanzahl),
        stubeschrieb: (newEntry.stubeschrieb || '').trim(),
        stuverrechnet: false,
        stuzuschlag: 0,
        studebidref: null,
        stuteiidref: null,
        stumank: false,
        stumanksatz: null,
        stumankart: ''
      };

      console.log('Sende neuen Eintrag an API:', apiEntry);

      const response = await fetch(`${API_BASE_URL}/tenant/arbeitszeiten`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Tenant-Key': user.tenant || user.database
        },
        body: JSON.stringify(apiEntry),
      });

      console.log('API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers])
      });

      if (!response.ok) {
        let errorMessage = 'Fehler beim Speichern des Eintrags';
        try {
          const errorData = await response.json();
          console.error('API-Fehler Details:', errorData);
          errorMessage = errorData.message || errorData.error || errorMessage;
          if (response.status === 500) {
            console.error('Vollständige Fehlermeldung:', errorData);
            const rawText = await response.text().catch(() => 'Kein Raw-Text verfügbar');
            console.error('Raw Response:', rawText);
          }
        } catch (e) {
          const errorText = await response.text();
          console.error('API-Fehler (raw):', errorText);
          errorMessage += `: ${errorText}`;
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      // API gibt { success: true, data: {...} } zurück
      const savedEntry = responseData.data || responseData;
      console.log('Gespeicherter Eintrag:', savedEntry);

      // Konvertiere die API-Antwort zurück in das erwartete Format (mit korrekten Feldnamen)
      // Datum: Server gibt jetzt bereits YYYY-MM-DD String zurück
      const stuDatum = savedEntry.studatum || newEntry.studatum;

      const processedEntry = {
        StuPGID: savedEntry.stuwebid,
        StuDatum: stuDatum,
        StuAnzahl: parseFloat(savedEntry.stuanzahl) || 0,
        StuBeschrieb: savedEntry.stubeschrieb || '',
        StuAufIDRef: savedEntry.stuaufidref,
        StuSarIDRef: savedEntry.stusaridref,
        StuErfassDatum: savedEntry.stuerfassdatum,
        StuVerrechnet: savedEntry.stuverrechnet,
        StuZuschlag: savedEntry.stuzuschlag,
        StuMank: savedEntry.stumank
      };

      console.log('Verarbeiteter Eintrag:', processedEntry);

      // Lokales Backup speichern
      saveEntryToBackup(user.tenant || user.database, user.mitid, apiEntry, 'created');

      // Prüfe ob das Datum in der aktuell angezeigten Woche liegt
      const { startDate: weekStart, endDate: weekEnd } = getWeekDates(currentWeek);
      const weekStartStr = formatDateForAPI(weekStart);
      const weekEndStr = formatDateForAPI(weekEnd);

      if (stuDatum >= weekStartStr && stuDatum <= weekEndStr) {
        // Eintrag liegt in der aktuellen Woche - direkt anzeigen
        setEntries(prevEntries => [...prevEntries, processedEntry].sort((a, b) =>
          new Date(a.StuDatum) - new Date(b.StuDatum)
        ));
      } else {
        // Eintrag liegt in einer anderen Woche - Hinweis anzeigen
        const dateParts = stuDatum.split('-');
        const displayDate = `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;
        setError(`Eintrag gespeichert. Der Eintrag vom ${displayDate} ist in der entsprechenden Woche sichtbar.`);
        setTimeout(() => setError(null), 5000);
      }
      setShowNewEntryForm(false);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError(`Fehler beim Speichern: ${err.message}`);
    }
  };

  const handleEdit = (entry) => {
    // Prüfe ob der Eintrag bearbeitet werden darf
    if (!isDateWithinRange(entry.StuDatum)) {
      setError('Einträge können nur innerhalb der letzten 10 Tage bearbeitet werden');
      return;
    }
    setError(null);
    setEditingEntry(entry);
    setShowNewEntryForm(false);
  };

  const handleEditSubmit = async (updatedEntry) => {
    try {
      // Validiere die Daten
      if (!updatedEntry.stuaufidref || !updatedEntry.stusaridref || !updatedEntry.studatum || !updatedEntry.stuanzahl) {
        throw new Error('Fehlende Pflichtfelder');
      }

      // Prüfe das Datum
      if (!isDateWithinRange(updatedEntry.studatum)) {
        throw new Error('Einträge können nur innerhalb der letzten 10 Tage bearbeitet werden');
      }

      const response = await fetch(`${API_BASE_URL}/tenant/arbeitszeiten/${updatedEntry.stuwebid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Key': user.tenant || user.database
        },
        body: JSON.stringify({
          ...updatedEntry,
          stumitidref: parseInt(user.mitid),
          stuanzahl: parseFloat(updatedEntry.stuanzahl),
          stuaufidref: parseInt(updatedEntry.stuaufidref),
          stusaridref: parseInt(updatedEntry.stusaridref)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Aktualisieren des Eintrags');
      }

      const savedEntry = await response.json();
      console.log('Gespeicherter Eintrag (raw):', savedEntry);
      console.log('Gesendetes Datum:', updatedEntry.studatum);

      // Konvertiere die API-Antwort zurück in das erwartete Format
      // savedEntry kann direkt das Objekt sein oder in data gewrappt
      const entryData = savedEntry.data || savedEntry;

      // Datum korrekt aus ISO-String extrahieren (Timezone-Korrektur)
      // Nur den Datumsteil (YYYY-MM-DD) extrahieren, um Timezone-Probleme zu vermeiden
      let stuDatum = updatedEntry.studatum; // Fallback: gesendetes Datum verwenden
      if (entryData.studatum) {
        stuDatum = entryData.studatum.split('T')[0];
        console.log('Datum vom Server:', entryData.studatum, '→ Extrahiertes Datum:', stuDatum);
      }

      const processedEntry = {
        StuPGID: entryData.stuwebid,
        StuDatum: stuDatum,
        StuAnzahl: parseFloat(entryData.stuanzahl) || 0,
        StuBeschrieb: entryData.stubeschrieb || '',
        StuAufIDRef: entryData.stuaufidref,
        StuSarIDRef: entryData.stusaridref,
        StuErfassDatum: entryData.stuerfassdatum,
        StuVerrechnet: entryData.stuverrechnet,
        StuZuschlag: entryData.stuzuschlag,
        StuMank: entryData.stumank
      };

      // Lokales Backup speichern
      saveEntryToBackup(user.tenant || user.database, user.mitid, updatedEntry, 'updated');

      setEntries(prevEntries =>
        prevEntries.map(entry =>
          entry.StuPGID === processedEntry.StuPGID ? processedEntry : entry
        )
      );
      setEditingEntry(null);
      setError(null);
    } catch (err) {
      console.error('Fehler beim Aktualisieren:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/tenant/arbeitszeiten/${entryId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Key': user.tenant || user.database
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Fehler beim Löschen des Eintrags');
      }

      // Lokales Backup speichern
      const deletedEntry = entries.find(e => e.StuPGID === entryId);
      saveEntryToBackup(user.tenant || user.database, user.mitid, deletedEntry || { stuwebid: entryId }, 'deleted');

      // Eintrag aus der Liste entfernen
      setEntries(prevEntries => prevEntries.filter(entry => entry.StuPGID !== entryId));
      setEditingEntry(null);
      setError(null);
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return <div className="loading-message">Lade Daten...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <WeekNavigation
        selectedWeek={currentWeek}
        onWeekChange={setCurrentWeek}
        user={user}
        onLogout={onLogout}
      />

      <main style={{padding: "0 5px"}}>
        <div className="mb-4">
          <button
            onClick={() => setShowNewEntryForm(true)}
            className="btn btn-primary"
            disabled={loading}
          >
            Neuer Eintrag
          </button>

          {/* Admin-Benutzer können auf /admin zugreifen */}
          {(user?.role === 'admin' || user?.mitadmin) && (
            <a
              href="/admin"
              className="btn btn-secondary"
              style={{marginLeft: '10px', display: 'inline-block', textDecoration: 'none'}}
            >
              Admin-Bereich
            </a>
          )}
        </div>

        {/* Loading und Error Anzeige */}
        {loading && (
          <div className="loading-message">
            Daten werden geladen...
          </div>
        )}
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <EntriesTable
            entries={entries}
            stundenarten={stundenarten}
            auftraege={auftraege}
            onEdit={handleEdit}
            onDelete={handleDelete}
            editingEntry={null}
            onEditSubmit={handleEditSubmit}
            onCancelEdit={() => setEditingEntry(null)}
            viewMode={viewMode}
            onToggleView={toggleViewMode}
          />

        {/* Popup für neuen Eintrag */}
        {showNewEntryForm && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Neuer Eintrag</h2>
              <NewEntryForm
                onSubmit={handleNewEntry}
                onCancel={() => setShowNewEntryForm(false)}
                auftraege={auftraege}
                stundenarten={stundenarten}
                auftragUsage={auftragUsage}
              />
            </div>
          </div>
        )}

        {/* Popup für Bearbeitung */}
        {editingEntry && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Eintrag bearbeiten</h2>
              <EditEntryForm
                entry={editingEntry}
                onSubmit={handleEditSubmit}
                onCancel={() => setEditingEntry(null)}
                onDelete={handleDelete}
                auftraege={auftraege}
                stundenarten={stundenarten}
                auftragUsage={auftragUsage}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TimeTrackingApp;